import React, { useContext } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import { useTreeItem, UseTreeItemParameters } from '@mui/x-tree-view/useTreeItem'
import {
  TreeItemContent,
  TreeItemIconContainer,
  TreeItemGroupTransition,
  TreeItemLabel,
  TreeItemRoot,
} from '@mui/x-tree-view/TreeItem'
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon'
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider'
import {
  getNodeIcon,
  getNodeColor,
  getNodeTitle,
} from '../../utils/nodeTypes'
import { CBT_SIDE_META_KEY, CBT_DESCRIPTION_META_KEY } from '../../utils/multitables'
import { TreeItemDataContext } from './utils'

interface CustomTreeItemProps extends Omit<UseTreeItemParameters, 'rootRef'>, Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {
  onFocus?: React.FocusEventHandler<HTMLLIElement> | null;
}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const { id, itemId, label, disabled, children, ...other } = props

  const {
    treeData,
    getNodeActions,
    getNodeTooltip,
    getCheckboxState,
    onNodeCheckedToggle,
    showTooltips,
    tooltipsHidden,
    disabled: treeDisabled,
  } = useContext(TreeItemDataContext)
  
  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })

  // Get the original node data and icon info from context
  const itemData = treeData.get(itemId)
  const originalNode = itemData?.originalNode

  if(!originalNode) {
    return null
  }

  const checkedState = getCheckboxState?.(originalNode)
  const customTooltip = getNodeTooltip?.(originalNode)

  // Get CBT metadata from meta for Table nodes
  const cbtSide = originalNode.type === 'Table'
    ? originalNode.meta?.find(m => m.key === CBT_SIDE_META_KEY)?.value
    : undefined
  const cbtDescription = originalNode.type === 'Table'
    ? originalNode.meta?.find(m => m.key === CBT_DESCRIPTION_META_KEY)?.value
    : undefined

  // Prefer side axis, fall back to description
  const subtitleText = cbtSide || cbtDescription

  const labelElement = subtitleText ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', overflow: 'hidden' }}>
      <TreeItemLabel {...getLabelProps()} sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        width: '100%',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.2
      }} />
      <Typography
        variant="caption"
        sx={{
          color: 'grey.500',
          fontSize: '11px',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
          width: '100%',
          mt: 0.25,
        }}
      >
        {subtitleText}
      </Typography>
    </Box>
  ) : (
    <TreeItemLabel {...getLabelProps()} sx={{
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
      width: '100%',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.2
    }} />
  )

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      {/* The node IS the control. There is no typeable variable field anywhere
          in the Carbon ingest flow — DataExplorer's "Variable" TextField is
          `disabled` and only echoes the selection — so clicking this row is the
          ONLY way a browser test can choose a variable. Keyed by the node's own
          identifier (`value1`, which for a Variable node is the variable name)
          rather than its label, which is survey copy; the tree item id is the
          fallback for node types that carry no value1, but it is a parent-path
          composite that also grows a `-level<n>` suffix while a search is
          active, so prefer value1 wherever it exists. */}
      <TreeItemRoot
        {...getRootProps(other)}
        data-testid={`tree-node-${originalNode.value1 || itemId}`}
      >
        <TreeItemContent {...getContentProps()} sx={{
          display: 'flex',
          alignItems: subtitleText ? 'flex-start' : 'center',
          width: '100%',
          overflow: 'hidden',
          minHeight: subtitleText ? '36px' : '22px',
          py: subtitleText ? '4px' : '6px',
          backgroundColor: status.selected ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: status.selected ? 'action.selected' : 'action.hover',
          },
          // A node action marked .tree-hover-action (the agent tabs' preview
          // eye, Adam 10 Sep) appears only while the row is hovered or holds
          // focus, so the tree reads as a tree, not a row of buttons.
          '& .tree-hover-action': { opacity: 0, transition: 'opacity 120ms ease' },
          '&:hover .tree-hover-action, &:focus-within .tree-hover-action': { opacity: 1 },
        }}>
          <TreeItemIconContainer {...getIconContainerProps()}>
            <TreeItemIcon status={status} />
          </TreeItemIconContainer>
          {
            !checkedState?.enabled && checkedState?.reason ? (
              <Tooltip title={checkedState.reason}>
                {/* A greyed box must not select the row: in Select mode that would open the table. */}
                <span style={{ display: 'inline-flex', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={checkedState.checked}
                    disabled
                    inputProps={{ 'aria-label': `Select ${getNodeTitle(originalNode)}` }}
                    size="medium"
                    sx={{ p: 0, mr: 0, '& .MuiSvgIcon-root': { fontSize: 20 } }}
                  />
                </span>
              </Tooltip>
            ) : checkedState?.enabled ? (
              <Checkbox
                checked={checkedState?.checked}
                disabled={treeDisabled}
                inputProps={{ 'aria-label': `Select ${getNodeTitle(originalNode)}` }}
                onChange={(e) => {
                  onNodeCheckedToggle?.(itemId, originalNode, e.target.checked)
                  e.stopPropagation()
                }}
                // this event setup is so we can have a normal checkbox here and it doesn't do it's odd "select all the parents" thing
                onClick={(e) => {
                  e.stopPropagation()
                }}
                onMouseDown={(e) => {
                  e.stopPropagation()
                }}
                onKeyDown={(e) => {
                  e.stopPropagation()
                }}
                size="medium"
                sx={{
                  p: 0,
                  mr: 0,
                  flexShrink: 0,
                  '& .MuiSvgIcon-root': { fontSize: 20 }
                }}
              />
            ) : checkedState && (originalNode.type === 'Variable' || originalNode.type === 'Code' || originalNode.type === 'Codeframe' || originalNode.type === 'Net' || originalNode.type === 'Arith') ? (
              // Render a spacer with the same dimensions as the checkbox when disabled
              // Only for node types that normally support checkboxes (not Folders)
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  p: 0,
                  mr: 0,
                  flexShrink: 0,
                }}
              />
            ) : null
          }
          <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem', color: getNodeColor(originalNode), flexShrink: 0 }}>
            {getNodeIcon(originalNode, status.expanded ? true : false)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, ml: -0.5 }}>
            {
              customTooltip && !tooltipsHidden ? (
                <Tooltip title={customTooltip} placement="right" slotProps={{ tooltip: { sx: { maxHeight: '80vh', overflowY: 'auto' } } }}>
                  {labelElement}
                </Tooltip>
              ) : subtitleText && !tooltipsHidden ? (
                <Tooltip title={subtitleText} placement="right" slotProps={{ tooltip: { sx: { maxHeight: '80vh', overflowY: 'auto' } } }}>
                  {labelElement}
                </Tooltip>
              ) : showTooltips ? (
                <Tooltip title={label} placement="right" slotProps={{ tooltip: { sx: { maxHeight: '80vh', overflowY: 'auto' } } }}>
                  {labelElement}
                </Tooltip>
              ) : (
                labelElement
              )
            }
          </Box>
          {getNodeActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0 }}>
              {getNodeActions(originalNode)}
            </Box>
          )}
        </TreeItemContent>
        {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
      </TreeItemRoot>
    </TreeItemProvider>
  )
})

export default CustomTreeItem